import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  BarChart3,
  BookOpenCheck,
  Clock3,
  FileCheck2,
  GraduationCap,
  Headphones,
  Mic2,
  PencilLine,
  Play,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";

const mockTests = [
  {
    title: "Full Mock Test",
    time: "136 min",
    path: "/mockpage/fullmocktest",
    resultpath: "/mockpage/result",
    icon: GraduationCap,
    category: "Complete exam",
    detail: "Speaking, writing, reading, and listening",
    panel: "border-violet-200 bg-white",
    iconStyle: "bg-violet-50 text-violet-700 ring-1 ring-violet-100",
    accent: "bg-violet-600",
  },
  {
    title: "Listening Mock Test",
    time: "33 min",
    path: "/mockpage/listeningmocktest",
    resultpath: "/mockpage/listeningresult",
    icon: Headphones,
    category: "Listening",
    detail: "Focused section practice",
    panel: "border-purple-200 bg-purple-50",
    iconStyle: "bg-purple-100 text-purple-700",
    accent: "bg-purple-400",
  },
  {
    title: "Speaking Mock Test",
    time: "36 min",
    path: "/mockpage/speakingmocktest",
    resultpath: "/mockpage/speakingresult",
    icon: Mic2,
    category: "Speaking",
    detail: "Focused section practice",
    panel: "border-green-200 bg-green-50",
    iconStyle: "bg-green-100 text-green-700",
    accent: "bg-green-400",
  },
  {
    title: "Reading Mock Test",
    time: "24 min",
    path: "/mockpage/readingmocktest",
    resultpath: "/mockpage/readingresult",
    icon: BookOpenCheck,
    category: "Reading",
    detail: "Focused section practice",
    panel: "border-orange-200 bg-orange-50",
    iconStyle: "bg-orange-100 text-orange-700",
    accent: "bg-orange-400",
  },
  {
    title: "Writing Mock Test",
    time: "40 min",
    path: "/mockpage/writingmocktest",
    resultpath: "/mockpage/writingresult",
    icon: PencilLine,
    category: "Writing",
    detail: "Focused section practice",
    panel: "border-blue-200 bg-blue-50",
    iconStyle: "bg-blue-100 text-blue-700",
    accent: "bg-blue-400",
  },
];

const summaryStats = [
  { label: "Mock types", value: "5" },
  { label: "Section tests", value: "4" },
  { label: "Full exam", value: "136 min" },
];

const MockPageShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const MockTestCard = ({ test, featured = false, onStart, onResult }) => {
  const Icon = test.icon;

  return (
    <article
      className={`group relative flex min-h-[258px] flex-col overflow-hidden rounded-md border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        test.panel
      } ${featured ? "lg:min-h-[340px] lg:p-6" : ""}`}
      aria-label={test.title}
    >
      <div className={`absolute inset-x-0 top-0 h-1 ${test.accent}`} />

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold uppercase text-gray-500">
            {test.category}
          </p>
          <h2 className={`${featured ? "text-2xl sm:text-3xl" : "text-xl"} mt-2 font-extrabold text-gray-900`}>
            {test.title}
          </h2>
          <p className={`${featured ? "max-w-xl text-base" : "text-sm"} mt-2 text-gray-500`}>
            {test.detail}
          </p>
        </div>

        <span
          className={`grid h-12 w-12 shrink-0 place-items-center rounded-md shadow-sm ${test.iconStyle}`}
        >
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-md bg-white/80 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-black/5">
          <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
          {test.time}
        </span>
        <span className="inline-flex items-center gap-2 rounded-md bg-white/80 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-black/5">
          <FileCheck2 className="h-4 w-4 text-purple-700" aria-hidden="true" />
          Timed mock
        </span>
        {featured && (
          <span className="inline-flex items-center gap-2 rounded-md bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-800 shadow-sm ring-1 ring-violet-100">
            Full-screen exam mode
          </span>
        )}
      </div>

      {featured && (
        <div className="mt-6 grid gap-3 text-sm sm:grid-cols-3">
          {["System checks", "4 exam sections", "Detailed report"].map((item) => (
            <div key={item} className="rounded-md bg-slate-50 p-3 font-semibold text-gray-700 ring-1 ring-slate-100">
              {item}
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto pt-6">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={onStart}
            className={`${featured ? "min-h-12" : "min-h-11"} inline-flex items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2`}
          >
            <Play className="h-4 w-4" aria-hidden="true" />
            Start
          </button>
          <button
            type="button"
            onClick={onResult}
            className={`${featured ? "min-h-12" : "min-h-11"} inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-bold text-gray-800 shadow-sm ring-1 ring-violet-200 transition hover:bg-violet-50 hover:text-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2`}
          >
            <BarChart3 className="h-4 w-4" aria-hidden="true" />
            Results
          </button>
        </div>
      </div>
    </article>
  );
};

export default function MockPage() {
  const navigate = useNavigate();
  const [featuredTest, ...sectionTests] = mockTests;

  const handleButtonClick = (path) => {
    navigate(path);
  };

  return (
    <MockPageShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Mock Test
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            Choose your next exam run
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Start a complete PTE mock or jump into a focused section test.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:min-w-[390px]">
          {summaryStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-md border border-slate-200 bg-white px-3 py-3 text-center shadow-sm"
            >
              <p className="text-lg font-extrabold text-gray-900">
                {stat.value}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase text-gray-500">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(360px,1.05fr),1.35fr]">
        <MockTestCard
          test={featuredTest}
          featured
          onStart={() => handleButtonClick(featuredTest.path)}
          onResult={() => handleButtonClick(featuredTest.resultpath)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {sectionTests.map((test) => (
            <MockTestCard
              key={test.title}
              test={test}
              onStart={() => handleButtonClick(test.path)}
              onResult={() => handleButtonClick(test.resultpath)}
            />
          ))}
        </div>
      </section>

      <section className="mt-6 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
              <FileCheck2 className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-semibold uppercase text-gray-500">
                Recent results
              </p>
              <h2 className="text-xl font-extrabold text-gray-900">
                Review completed mock tests
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleButtonClick("/mockpage/result")}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
          >
            Open results
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </section>
    </MockPageShell>
  );
}
